import hokkaidoAirportMtxApi from '@/apis/externalReports/hokkaidoAirport/hokkaido_airport_mtx';
import type {
  HokkaidoAirportReportWithPhotos,
  HokkaidoAirportReportExt,
} from '@/models/externalReports/hokkaidoAirport/hokkaidoAirportReport';
import type { CarInfo, SearchConditions } from '@/models/externalReports/hokkaidoAirport/hokkaidoAirportMap';
import type {
  HokkaidoAirportCarExt,
  HokkaidoAirportCarShow,
} from '@/models/externalReports/hokkaidoAirport/hokkaidoAirportCar';
import type {
  HokkaidoAirportSettings,
} from '@/models/apis/externalReports/hokkaidoAirport/hokkaidoAirportSettings';

const maxDateRange = 31;
const oneDayMsec = 1000 * 60 * 60 * 24;

interface ConvDataParams {
  cars: HokkaidoAirportCarShow[];
  currentCarInfoMap: Record<number, CarInfo>;
  userSettings: HokkaidoAirportSettings;
}
export type CurrentReportItemInfo = Pick<
  CarInfo,
  'selectedPhotoId' | 'selectedMtxTs' | 'savedImageMap' | 'loadedMtxs'
>;

export function initSearchConds(): SearchConditions {
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  return {
    hanName: null,
    status: '全て',
    dateFrom: new Date(now.getTime() - oneDayMsec * maxDateRange),
    dateTo: now,
  };
}

export function validSearchDateRange(searchConds: SearchConditions): boolean {
  const from = searchConds.dateFrom.getTime();
  const to = searchConds.dateTo.getTime();
  return Number.isFinite(from) && Number.isFinite(to) &&
    from <= to && (to - from) <= maxDateRange * oneDayMsec;
}

export function createInitialCarsData(recentData: HokkaidoAirportCarShow[]): HokkaidoAirportCarExt[] {
  return recentData.map(car => {
    return {
      ...car,
      isMoving: false,
      isWorking: false,
      shouldShow: true,
      isSelected: false,
      pos: null,
      reportExt: null,
    };
  });
}

export function convData(params: ConvDataParams): HokkaidoAirportCarExt[] {
  return params.cars.map(car => {
    let reportExt: HokkaidoAirportReportExt | null = null;
    const tmpCar = params.currentCarInfoMap[car.id];
    if (car.report) {
      const itemInfo = tmpCar ? {
        selectedPhotoId: tmpCar.selectedPhotoId,
        selectedMtxTs: tmpCar.selectedMtxTs,
        savedImageMap: tmpCar.savedImageMap,
        loadedMtxs: tmpCar.loadedMtxs,
      } : undefined;
      reportExt = convReport(car.report, itemInfo);
    }
    const shouldShow = true;
    const statusChoices = params.userSettings.choices.status;
    const isWorking = car.status === statusChoices.working.key;
    // tsIsNewがfalseの場合は、停止中とみなす
    const isMoving = car.tsIsNew && (isWorking || car.status === statusChoices.running.key);
    const isSelected = tmpCar?.isSelected ?? false;

    // posはLayerManagerで変換して設定する
    return {
      ...car,
      isMoving,
      isWorking,
      shouldShow,
      isSelected,
      pos: null,
      reportExt,
    };
  });
}

export function shouldShowCar(
  car: HokkaidoAirportCarExt,
  decidedSearchConds: SearchConditions,
): boolean {
  // 検索条件に照らして車を表示すべきか判定
  if (decidedSearchConds.hanName &&
    car.hanName !== decidedSearchConds.hanName) {
    return false;
  }
  if (decidedSearchConds.status !== '' && decidedSearchConds.status !== '全て') {
    if (car.status !== decidedSearchConds.status) {
      return false;
    }
  }

  return true;
}

export const convReport = (
  report: HokkaidoAirportReportWithPhotos,
  currentReportItemInfo?: CurrentReportItemInfo,
): HokkaidoAirportReportExt => {
  const loadedMtxs = currentReportItemInfo?.loadedMtxs || [];
  const reportExt: HokkaidoAirportReportExt = {
    ...report,
    photosExt: report.photos.map(photo => ({
      ...photo,
      isSelected: currentReportItemInfo?.selectedPhotoId === photo.id,
      hanName: report.hanName ?? '', // Ensure hanName is always a string
      airportName: report.airportName ?? '',
      standbyType: report.standbyType ?? '',
      comment: photo.comment,
      savedImage: null,
      samePosPhotos: [],
      workCycles: report.workCycles || [],
    })),
    mtxsExt: loadedMtxs,
    isSelected: false,
    isMtxsLoaded: loadedMtxs.length > 0,
  };
  for (const photo of reportExt.photosExt) {
    // 取得済みの画像は再取得しない
    photo.savedImage = currentReportItemInfo?.savedImageMap?.[photo.id] ?? null;
    photo.samePosPhotos = reportExt.photosExt.filter(
      (e) => e.lat === photo.lat && e.lon === photo.lon,
    );
  }
  return reportExt;
};

export const loadHokkaidoAirportMtxs = async(
  reports: HokkaidoAirportReportExt[],
): Promise<void> => {
  try {
    const hokkaidoAirportMtxs = await hokkaidoAirportMtxApi.getMtxsByReportIds({
      report_ids: reports.map(report => report.id),
    });
    reports.forEach(report => {
      if (!hokkaidoAirportMtxs[report.id]) return;
      report.mtxsExt = hokkaidoAirportMtxs[report.id].map(mtx => {
        return {
          ...mtx,
          isSelected: false,
          hanName: report.hanName,
          airportName: report.airportName,
          standbyType: report.standbyType,
        };
      });
      report.isMtxsLoaded = true;
    });
  } catch (e) {
    console.error('loadStandardMtxs failed', e);
  }
};

export const deselectReportItems = (report: HokkaidoAirportReportExt): void => {
  report.photosExt.forEach(photo => {
    photo.isSelected = false;
  });
  report.mtxsExt.forEach(mtx => {
    mtx.isSelected = false;
  });
};

export const convSearchParamTsTo = (dateTo: Date): Date => {
  return new Date(dateTo.getTime() + oneDayMsec - 1);
};
